<template>
  <div class="article-skeleton" aria-hidden="true">
    <!-- Hero image – hidden on mobile (matches article-page.vue) -->
    <div class="hero-wrap">
      <div class="skeleton-hero shimmer" />
    </div>

    <!-- Title -->
    <div class="title-area">
      <div class="skel-line shimmer" style="width: 96%; height: 28px" />
      <div class="skel-line shimmer" style="width: 70%; height: 28px; margin-top: 12px" />
    </div>

    <!-- Date – hidden on mobile -->
    <div class="subtitle-area">
      <div class="skel-line shimmer" style="width: 110px; height: 15px" />
    </div>

    <!-- Paragraph blocks (3 blocks, varying line counts) -->
    <div class="body-area">
      <template v-for="(block, bi) in blocks" :key="bi">
        <div class="skeleton-block">
          <div
            v-for="(w, li) in block"
            :key="li"
            class="skel-line shimmer"
            :style="{ width: w, height: '20px' }"
          />
        </div>
        <div
          v-if="bi < blocks.length - 1"
          class="block-divider shimmer"
        />
      </template>
    </div>

    <!-- Bottom nav buttons -->
    <div class="bottom-actions-skel">
      <div class="skel-btn shimmer" />
      <div class="skel-btn skel-btn--wide shimmer" />
      <div class="skel-btn shimmer" />
    </div>
  </div>
</template>

<script setup lang="ts">
const blocks = [
  ['100%', '100%', '100%', '88%'],
  ['100%', '100%', '95%', '100%', '62%'],
  ['100%', '100%', '80%'],
]
</script>

<style scoped>
.article-skeleton {
  padding-bottom: var(--section-gap, 22px);
  animation: fade-in 0.15s ease both;
}

@keyframes fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

/* Hero image – matches .article-hero + .hero-image */
.hero-wrap {
  padding: 16px var(--content-padding-x, 22px) 0;
  display: flex;
  justify-content: center;
}

.skeleton-hero {
  width: 380px;
  height: 220px;
  border-radius: 8px;
  flex-shrink: 0;
}

/* Title area – matches .title padding */
.title-area {
  padding: 15px var(--content-padding-x, 22px) 0;
  display: flex;
  flex-direction: column;
}

/* Date – matches .subtitle padding */
.subtitle-area {
  padding: 12px var(--content-padding-x, 22px);
}

/* Body */
.body-area {
  padding: 0 var(--content-padding-x, 22px);
}

.skeleton-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 24px 0;
}

.block-divider {
  width: min(680px, 100%);
  height: 1px;
  margin: 0 auto 14px;
  border-radius: 1px;
}

/* Bottom navigation placeholder */
.bottom-actions-skel {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 40px 0 60px;
}

.skel-btn {
  height: 44px;
  width: 110px;
  border-radius: 8px;
  flex-shrink: 0;
}

.skel-btn--wide {
  width: 200px;
}

/* Shimmer */
.skel-line,
.skeleton-hero,
.block-divider,
.skel-btn {
  background: linear-gradient(
    110deg,
    #efefef 8%,
    #e0e0e0 18%,
    #efefef 33%
  );
  background-size: 800px 100%;
  animation: shimmer 1.8s infinite linear;
}

/* Slightly different shade for divider */
.block-divider {
  background: linear-gradient(
    110deg,
    #e5e5e5 8%,
    #d8d8d8 18%,
    #e5e5e5 33%
  );
}

@keyframes shimmer {
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}

/* Responsive – match article-page.vue breakpoints */
@media (max-width: 800px) {
  .skeleton-hero {
    width: 100%;
    max-width: 320px;
    height: 180px;
  }
  .skeleton-block { gap: 8px; margin: 16px 0; }
  .skel-line { height: 18px !important; }
  .bottom-actions-skel { padding: 26px 0 40px; }
  .skel-btn { height: 40px; flex: 1 1 0; }
  .skel-btn--wide { width: auto; }
}

@media (max-width: 576px) {
  /* Hidden in article-page.vue */
  .hero-wrap   { display: none; }
  .subtitle-area { display: none; }
}
</style>
